import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, ArrowLeft, Save, Loader2 } from 'lucide-react';
import type { EventPackage } from '@natsepa/database';
import { ImageManager, type ManagedImage } from '../../components/ImageManager';
import { BilingualField } from '../../components/BilingualField';
import { BilingualArrayField } from '../../components/BilingualArrayField';

// Helper function to generate slug from title
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function EventsList() {
  const { data, isLoading, error } = useList<EventPackage>({ resource: 'events' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this event?')) {
      deleteOne({ resource: 'events', id });
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error loading events: {error.message}</p>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Events</h1>
        <button onClick={() => create('events')} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors">
          <Plus className="w-5 h-5" /> Add Event
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Capacity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((event) => (
              <tr key={event.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{event.name}</div>
                  <div className="text-sm text-gray-500">{event.slug}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{event.capacity ? `${event.capacity} guests` : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{event.price ? `IDR ${event.price.toLocaleString()}` : '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => edit('events', event.id)} className="p-2 text-gray-400 hover:text-gold-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(event.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.data.length === 0 && <div className="p-8 text-center text-gray-500">No events found. Click "Add Event" to create one.</div>}
      </div>
    </div>
  );
}

// Create Event
export function EventsCreate() {
  const navigate = useNavigate();
  const { mutate: createEvent, isPending } = useCreate();
  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    eventType: 'mice',
    price: 0,
    capacity: 0,
    includes: [] as { en: string; id: string }[],
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nameId) { alert('Please fill in the name'); return; }
    const slug = form.slug || generateSlug(form.nameId);
    createEvent(
      { resource: 'events', values: { ...form, slug, name: form.nameId || form.nameEn, description: form.descriptionId || form.descriptionEn, eventType: form.eventType } },
      { onSuccess: (data) => { const newId = data?.data?.id; navigate(newId ? `/events/edit/${newId}` : '/events'); } }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/events')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Event Package</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new event or wedding package</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={form.nameId ? generateSlug(form.nameId) : 'auto-generated'} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Event Details</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
              {['wedding', 'mice', 'birthday', 'romantic-dinner', 'ibadah'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
              <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (guests)</label>
              <input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800"><strong>Note:</strong> You can add images after creating the event.</p>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/events')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Event
export function EventsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<EventPackage>({ resource: 'events', id: id! });
  const { mutate: updateEvent, isPending } = useUpdate();

  const [form, setForm] = useState({
    slug: '',
    nameEn: '',
    nameId: '',
    descriptionEn: '',
    descriptionId: '',
    shortDescriptionEn: '',
    shortDescriptionId: '',
    eventType: 'mice',
    price: 0,
    capacity: 0,
    includes: [] as { en: string; id: string }[],
    terms: [] as { en: string; id: string }[],
    sortOrder: 0,
  });
  const [images, setImages] = useState<ManagedImage[]>([]);

  useEffect(() => {
    if (data?.data) {
      const event = data.data;
      const zipBi = (en: any[] | undefined, id: any[] | undefined) => {
        const e = Array.isArray(en) ? en : [];
        const d = Array.isArray(id) ? id : [];
        const len = Math.max(e.length, d.length);
        return Array.from({ length: len }, (_, i) => ({ en: e[i] || d[i] || '', id: d[i] || e[i] || '' }));
      };
      setForm({
        slug: event.slug || '',
        nameEn: (event as any).nameEn || (event as any).name_en || event.name || '',
        nameId: (event as any).nameId || (event as any).name_id || event.name || '',
        descriptionEn: (event as any).descriptionEn || (event as any).description_en || event.description || '',
        descriptionId: (event as any).descriptionId || (event as any).description_id || event.description || '',
        shortDescriptionEn: (event as any).shortDescriptionEn || (event as any).short_description_en || event.shortDescription || '',
        shortDescriptionId: (event as any).shortDescriptionId || (event as any).short_description_id || event.shortDescription || '',
        eventType: (event as any).eventType || (event as any).event_type || 'mice',
        price: event.price || 0,
        capacity: event.capacity || 0,
        sortOrder: (event as any).sortOrder ?? 0,
        includes: zipBi((event as any).includesEn, (event as any).includesId),
        terms: zipBi((event as any).termsEn, (event as any).termsId),
      });
      if (event.images) {
        setImages(event.images.map((url, index) => ({ url, imageType: 'gallery' as const, sortOrder: index })));
      }
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateEvent(
      { resource: 'events', id: id!, values: { ...form, eventType: form.eventType, images: images.map((img) => img.url) } },
      { onSuccess: () => navigate('/events') }
    );
  };

  if (isLoading) return <div className="p-4">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/events')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Event Package</h1>
          <p className="text-sm text-gray-500 mt-1">{form.nameId || form.nameEn || 'Event details'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
            <BilingualField label="Name" valueEn={form.nameEn} valueId={form.nameId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'nameEn' : 'nameId']: value })} required />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input type="text" value={form.slug} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-gray-50" readOnly />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Description</h2>
            <BilingualField label="Short Description" valueEn={form.shortDescriptionEn} valueId={form.shortDescriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'shortDescriptionEn' : 'shortDescriptionId']: value })} multiline rows={2} />
            <BilingualField label="Full Description" valueEn={form.descriptionEn} valueId={form.descriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })} multiline rows={4} />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Event Details</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
              <select value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                {['wedding', 'mice', 'birthday', 'romantic-dinner', 'ibadah'].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (IDR)</label>
                <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacity (guests)</label>
                <input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">What's Included</h2>
            <BilingualArrayField label="Includes" items={form.includes} onChange={(includes) => setForm({ ...form, includes })} />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Terms & Conditions</h2>
            <BilingualArrayField label="Terms" items={form.terms} onChange={(terms) => setForm({ ...form, terms })} />
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/events')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Event Images</h2>
            <ImageManager entityType="event" entityId={id!} images={images} imageType="gallery" multiple={true} maxImages={10} onChange={setImages} />
          </div>
        </div>
      </div>
    </div>
  );
}
